import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Modal, ModalHeader, ModalBody, Alert, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import CountUp from 'react-countup';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import api,{handleApiError} from '../../../helpers/api';

import TableContainer from '../../../Components/Common/TableContainer';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";

// Custom CSS for design inspired by AllOrders
const customStyles = `
  .demo-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  .demo-modal .modal-content {
    border-radius: 8px;
    border: none;
  }
  .demo-modal .modal-header {
    background: linear-gradient(135deg, #2a4966 0%, #0ab39c 100%);
    color: #ffffff;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding: 1.5rem;
  }
  .demo-modal .modal-header h5 {
    font-weight: 600;
    font-size: 1.4rem;
    color: #ffffff;
  }
  .demo-modal .modal-body {
    padding: 1.5rem;
    background-color: #f9fafb;
  }
  .demo-modal .modal-body p {
    color: #333;
    font-size: 1rem;
    line-height: 1.6;
  }
  .demo-modal .modal-body strong {
    color: #2a4966;
  }
  .demo-table th, .demo-table td {
    vertical-align: middle;
    padding: 0.75rem;
  }
  .demo-table tr:hover {
    background-color: #f1f5f7;
  }
  .demo-widget-card {
    transition: transform 0.3s ease;
  }
  .demo-widget-card:hover {
    transform: translateY(-5px);
  }
  .filter-container {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
    flex-wrap: nowrap;
    overflow-x: auto;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  }
  .filter-container .form-control,
  .filter-container .form-select {
    border-radius: 4px;
    border: 1px solid #ced4da;
    min-width: 150px;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }
  .filter-container .form-control:focus,
  .filter-container .form-select:focus {
    border-color: #0ab39c;
    box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25);
  }
  .action-btn {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
    transition: background-color 0.3s ease, transform 0.2s ease;
  }
  .action-btn:hover {
    transform: translateY(-2px);
  }
  .new-badge {
    margin-left: 0.5rem;
    font-size: 0.75rem;
    vertical-align: middle;
  }
  .card-header {
    background-color: transparent;
    border-bottom: 1px solid #e9ecef;
    padding: 1rem 1.5rem;
  }
  .card-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2a4966;
  }
  .dropdown-menu {
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: none;
  }
  .dropdown-item {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
    color: #2a4966;
  }
  .dropdown-item:hover {
    background-color: #f1f5f7;
    color: #0ab39c;
  }
  .dropdown-item i {
    margin-right: 0.5rem;
  }
  @media (max-width: 768px) {
    .demo-modal .modal-body {
      padding: 1rem;
    }
    .filter-container {
      flex-wrap: nowrap;
      overflow-x: auto;
    }
    .filter-container .form-control,
    .filter-container .form-select {
      min-width: 120px;
      font-size: 0.85rem;
    }
  }
`;

const DemoRequestList = () => {
    const { t } = useTranslation();
    document.title = t('demo_request_list.title');

    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewModal, setViewModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState({});
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fetchRequests = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/demandeDemo', {
                withCredentials: true,
            });
            setRequests(response.data.data);
            setFilteredRequests(response.data.data);
            setIsLoading(false);
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                await handleApiError(err, dispatch, navigate);
            } else {
                setError(t('demo_request_list.error_fetch'));
                setIsLoading(false);
                toast.error(t('demo_request_list.toast_error_fetch'));
            }
        }
    }, [t, dispatch, navigate]);
    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // Filter and Sort Requests
    useEffect(() => {
        let updatedRequests = [...requests];
        // Apply search filter
        if (searchQuery) {
            updatedRequests = updatedRequests.filter(request =>
                (request.nom?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
                (request.email?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
                (request.company?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
                (request.message?.toLowerCase().includes(searchQuery.toLowerCase()) || '')
            );
        }
        // Apply status filter
        if (filterStatus !== 'all') {
            updatedRequests = updatedRequests.filter(request => request.treated === (filterStatus === 'treated'));
        }
        // Apply sort order
        updatedRequests.sort((a, b) => {
            const dateA = new Date(a.dateSoumission || Date.now());
            const dateB = new Date(b.dateSoumission || Date.now());
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        setFilteredRequests(updatedRequests);
    }, [requests, filterStatus, sortOrder, searchQuery]);

    // Widgets data
    const requestWidgets = useMemo(() => [
        {
            title: t('demo_request_list.total_requests'),
            counter: requests.length,
            suffix: '',
            decimals: 0,
            percentage: '0%',
            percentageClass: 'success',
            arrowIcon: 'ri-arrow-up-line',
            icon: 'ri-question-line',
        },
        {
            title: t('demo_request_list.treated_requests'),
            counter: requests.filter(request => request.treated).length,
            suffix: '',
            decimals: 0,
            percentage: '0%',
            percentageClass: 'success',
            arrowIcon: 'ri-arrow-up-line',
            icon: 'ri-checkbox-circle-line',
        },
        {
            title: t('demo_request_list.untreated_requests'),
            counter: requests.filter(request => !request.treated).length,
            suffix: '',
            decimals: 0,
            percentage: '0%',
            percentageClass: 'danger',
            arrowIcon: 'ri-arrow-down-line',
            icon: 'ri-alert-line',
        },
    ], [requests, t]);

    const handleMarkTreated = async (request) => {
        try {
            await api.patch(
                `/demandeDemo/${request._id}/traiter`,
                { treated: !request.treated },
                { withCredentials: true }
            );
toast.success(`Request successfully marked as ${request.treated ? 'untreated' : 'treated'}`);
            fetchRequests();
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                await handleApiError(err, dispatch, navigate);
            } else {
                toast.error(t('demo_request_list.toast_error_treated'));
            }
        }
    };
    const handleDeleteRequest = async () => {
        try {
            await api.delete(`/demandeDemo/${selectedRequest._id}`, {
                withCredentials: true,
            });
            toast.success(t('demo_request_list.toast_success_delete'));
            setDeleteModal(false);
            fetchRequests();
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                await handleApiError(err, dispatch, navigate);
            } else {
                toast.error(t('demo_request_list.toast_error_delete'));
            }
        }
    };
    // Handle View
    const handleViewClick = (request) => {
        setSelectedRequest(request);
        setViewModal(true);
    };

    // Handle Delete
    const handleDeleteClick = (request) => {
        setSelectedRequest(request);
        setDeleteModal(true);
    };

    // Toggle dropdown for a specific row
    const toggleDropdown = (id) => {
        setDropdownOpen(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const isToday = (dateSoumission) => {
        const today = new Date();
        const requestDate = new Date(dateSoumission);
        return requestDate.toLocaleDateString() === today.toLocaleDateString();
    };

    // Table Columns
    const columns = useMemo(
        () => [
            {
                Header: t('demo_request_list.table.name'),
                accessor: 'nom',
                filterable: true,
                Cell: ({ value }) => <span>{value}</span>,
            },
            {
                Header: t('demo_request_list.table.email'),
                accessor: 'email',
                filterable: true,
                Cell: ({ value }) => <span>{value}</span>,
            },
            {
                Header: t('demo_request_list.table.company'),
                accessor: 'company',
                filterable: true,
                Cell: ({ value }) => <span>{value || '-'}</span>,
            },
            {
                Header: t('demo_request_list.table.message'),
                accessor: 'message',
                filterable: true,
                Cell: ({ value }) => <span>{value ? (value.length > 50 ? `${value.substring(0, 50)}...` : value) : '-'}</span>,
            },
            {
                Header: t('demo_request_list.table.budget'),
                accessor: 'budget',
                filterable: true,
                Cell: ({ value }) => <span>{value ? `${value.toLocaleString()} DT` : '-'}</span>,
            },
            {
                Header: t('demo_request_list.table.status'),
                accessor: 'treated',
                filterable: true,
                Cell: ({ value, row }) => (
                    <span>
                        <span className={`badge badge-soft-${value ? 'success' : 'warning'} text-uppercase`}>
                            {t(`demo_request_list.table.status_${value ? 'treated' : 'untreated'}`)}
                        </span>
                        {!value && isToday(row.original.dateSoumission) && (
                            <span className="badge badge-soft-info new-badge">{t('demo_request_list.table.new')}</span>
                        )}
                    </span>
                ),
            },
            {
                Header: t('demo_request_list.table.actions'),
                Cell: ({ row }) => (
                    <Dropdown
                        isOpen={dropdownOpen[row.original._id] || false}
                        toggle={() => toggleDropdown(row.original._id)}
                    >
                        <DropdownToggle tag="button" className="btn btn-soft-secondary action-btn">
                            <i className="ri-more-2-fill"></i>
                        </DropdownToggle>
                        <DropdownMenu>
                            <DropdownItem onClick={() => handleViewClick(row.original)}>
                                <i className="ri-eye-fill me-1"></i> {t('demo_request_list.table.action_view')}
                            </DropdownItem>
                            <DropdownItem onClick={() => handleMarkTreated(row.original)}>
                                <i className="ri-checkbox-circle-line me-1"></i>
                                {t(`demo_request_list.table.action_mark_${row.original.treated ? 'untreated' : 'treated'}`)}
                            </DropdownItem>
                            <DropdownItem onClick={() => handleDeleteClick(row.original)}>
                                <i className="ri-delete-bin-fill me-1"></i> {t('demo_request_list.table.action_delete')}
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                ),
            },
        ],
        [dropdownOpen, t]
    );

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('demo_request_list.title')} pageTitle={t('demo_request_list.page_title')} />
                    {/* Widgets Section */}
                    <Row>
                        {requestWidgets.map((item, key) => (
                            <Col xxl={4} sm={6} key={key}>
                                <Card className="demo-widget-card card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{item.title}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <span className="counter-value">
                                                        <CountUp
                                                            start={0}
                                                            end={item.counter}
                                                            duration={3}
                                                            suffix={item.suffix}
                                                            decimals={item.decimals}
                                                        />
                                                    </span>
                                                </h2>
                                                <p className="mb-0 text-muted">
                                                    <span className={`badge bg-light text-${item.percentageClass} mb-0`}>
                                                        <i className={`${item.arrowIcon} align-middle`}></i> {item.percentage}
                                                    </span> {t('demo_request_list.widget_vs_previous_month')}
                                                </p>
                                            </div>
                                            <div>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className="avatar-title bg-soft-info text-info rounded-circle fs-4">
                                                        <i className={item.icon}></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    {/* Demo Requests Table */}
                    <Row>
                        <Col lg={12}>
                            <Card className="demo-card">
                                <CardHeader className="d-flex align-items-center border-0">
                                    <h5 className="card-title mb-0 flex-grow-1">{t('demo_request_list.card_title')}</h5>
                                    <div className="flex-shrink-0">
                                        <div className="filter-container hstack gap-2">
                                            <Input
                                                type="text"
                                                placeholder={t('demo_request_list.search_placeholder')}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="form-control"
                                            />
                                            <Input
                                                type="select"
                                                className="form-select"
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="all">{t('demo_request_list.filter_all_statuses')}</option>
                                                <option value="treated">{t('demo_request_list.filter_treated')}</option>
                                                <option value="untreated">{t('demo_request_list.filter_untreated')}</option>
                                            </Input>
                                            <Input
                                                type="select"
                                                className="form-select"
                                                value={sortOrder}
                                                onChange={(e) => setSortOrder(e.target.value)}
                                            >
                                                <option value="desc">{t('demo_request_list.sort_newest')}</option>
                                                <option value="asc">{t('demo_request_list.sort_oldest')}</option>
                                            </Input>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <i className="ri-loader-2-line fs-24 text-primary spin"></i>
                                        </div>
                                    ) : error ? (
                                        <Alert color="danger">{error}</Alert>
                                    ) : (
                                        <TableContainer
                                            columns={columns}
                                            data={filteredRequests}
                                            isGlobalFilter={false}
                                            customPageSize={8}
                                            className="custom-header-css"
                                            divClass="table-responsive table-card mb-1"
                                            tableClass="align-middle table-nowrap"
                                            theadClass="table-light text-muted"
                                            thClass=""
                                        />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* View Modal */}
                    <Modal
                        isOpen={viewModal}
                        toggle={() => setViewModal(false)}
                        centered
                        className="demo-modal"
                    >
                        <ModalHeader
                            toggle={() => setViewModal(false)}
                            className="p-3"
                        >
                            {t('demo_request_list.modal_title')}
                        </ModalHeader>
                        <ModalBody>
                            {selectedRequest && (
                                <div>
                                    <h5 className="mb-3">{t('demo_request_list.modal_info')}</h5>
                                    <p><strong>{t('demo_request_list.modal_name')}:</strong> {selectedRequest.nom}</p>
                                    <p><strong>{t('demo_request_list.modal_email')}:</strong> {selectedRequest.email}</p>
                                    <p><strong>{t('demo_request_list.modal_company')}:</strong> {selectedRequest.company || '-'}</p>
                                    <p><strong>{t('demo_request_list.modal_message')}:</strong> {selectedRequest.message || '-'}</p>
                                    <p><strong>{t('demo_request_list.modal_budget')}:</strong> {selectedRequest.budget ? `$${selectedRequest.budget.toLocaleString()}` : '-'}</p>
                                    <p><strong>{t('demo_request_list.modal_status')}:</strong> {t(`demo_request_list.table.status_${selectedRequest.treated ? 'treated' : 'untreated'}`)}</p>
                                    {selectedRequest.dateSoumission && (
                                        <p>
                                            <strong>{t('demo_request_list.modal_submitted')}:</strong> {moment(selectedRequest.dateSoumission).format('DD/MM/YYYY HH:mm')}
                                        </p>
                                    )}
                                </div>
                            )}
                        </ModalBody>
                    </Modal>
                    {/* Delete Modal */}
                    <DeleteModal
                        show={deleteModal}
                        onDeleteClick={handleDeleteRequest}
                        onCloseClick={() => setDeleteModal(false)}
                    />
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default DemoRequestList;